/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import {
  ErrorTypes,
  type FeatureResponse,
  type TNpvrAllowRecording
} from "@tivocorp/data-domain-lib";

import { Log } from "../logging/Log";
import { DiagnosticLogger, OperationalLogger } from "../logging";
import type { IPostSigninInteractor } from "../interfaces";
import type { TNetworkOptions } from "../types/TNetworkOptions";
import { DiagnosticSeverity } from "../types";
import {
  MILLISECONDS_PER_MINUTE,
  PLAYBACK_INACTIVITY_OSD_TIMEOUT_MS
} from "../../config";
import { parseFeatureAttributeBoolean } from "./featureAttributeParsers";

export class DeviceFeatures {
  private static readonly TAG: string = "DeviceFeatures";

  public static readonly MSO_SERVICE_ID: string = "msoServiceId";

  public static readonly PLAYBACK_CONFIG: string = "playbackConfig";

  public static readonly INACTIVITY_TIME: string = "inactivityTime";

  private static readonly ATTRIBUTE_SWITCH_GROUP: string = "switchGroup";

  private static readonly ATTRIBUTE_AUTO_STANDBY_INACTIVITY_TIMEOUT_MINUTES: string =
    "autoStandbyInactivityTimeoutMinutes";

  private static readonly SWITCH_GROUP_DEFAULT_KEY: string = "default";

  private static mMsoServiceId: string = "";

  private static mIsMsoServiceIdValid: boolean = false;

  private static mIsMsoServiceIdChanged: boolean = false;

  private static mNpvrAllowRecording: TNpvrAllowRecording | null = null;

  private static mNpvrAllowRecordingTimestamp: number = 0;

  private static mSwitchGroups: Map<string, string> = new Map<string, string>();

  private static mIsSwitchGroupValid: boolean = false;

  private static mPlaybackInactivityTimeoutMs: number =
    PLAYBACK_INACTIVITY_OSD_TIMEOUT_MS;

  private static mIsPlaybackInactivityTimeoutValid: boolean = false;
  /**
   * All device features keyed by name, captured on each `loadDeviceFeatures`.
   * Backs the synchronous `isServiceFeatureEnabled` lookup for the Feature
   * Activation Framework without a second provisioning request or cache.
   */
  private static mServiceFeatures: Map<string, FeatureResponse> = new Map<
    string,
    FeatureResponse
  >();

  /** True once device features have been loaded at least once this session. */
  private static mHasLoadedServiceFeatures: boolean = false;

  public static initMsoServiceIdFeature(aMsoServiceId: FeatureResponse): void {
    if (
      aMsoServiceId === undefined ||
      aMsoServiceId === null ||
      aMsoServiceId.attributes === undefined ||
      aMsoServiceId.attributes === null
    ) {
      Log.w(
        DeviceFeatures.TAG,
        "initMsoServiceIdFeature() — feature or attributes missing"
      );
      return;
    }

    let msoServiceIdValue: string = "";
    for (let i = 0; i < aMsoServiceId.attributes.length; i += 1) {
      const attribute = aMsoServiceId.attributes[i];
      if (
        attribute.attributeName !== undefined &&
        attribute.attributeName !== null &&
        attribute.attributeName === "msoServiceIdName" &&
        attribute.attributeValue !== undefined &&
        attribute.attributeValue !== null
      ) {
        msoServiceIdValue = attribute.attributeValue;
        break;
      }
    }

    DeviceFeatures.mIsMsoServiceIdValid = msoServiceIdValue !== "";

    if (DeviceFeatures.mIsMsoServiceIdValid) {
      DeviceFeatures.mIsMsoServiceIdChanged =
        DeviceFeatures.mMsoServiceId !== "" &&
        msoServiceIdValue !== DeviceFeatures.mMsoServiceId;
      DeviceFeatures.mMsoServiceId = msoServiceIdValue;
      Log.d(
        DeviceFeatures.TAG,
        "initMsoServiceIdFeature() — msoServiceId initialized"
      );
    }
  }

  public static getMsoServiceId(): string {
    return DeviceFeatures.mMsoServiceId;
  }

  public static isMsoServiceIdValid(): boolean {
    return DeviceFeatures.mIsMsoServiceIdValid;
  }

  public static isMsoServiceIdChanged(): boolean {
    return DeviceFeatures.mIsMsoServiceIdChanged;
  }

  /**
   * Captures the full device-feature set for synchronous lookups. Called on
   * every `loadDeviceFeatures`, so it replaces (never merges) the previous set
   * and stays consistent with a re-login/profile refresh.
   *
   * @param {Array<FeatureResponse> | undefined} features - Device features
   */
  public static initServiceFeatures(
    features: Array<FeatureResponse> | undefined
  ): void {
    DeviceFeatures.mServiceFeatures = new Map<string, FeatureResponse>();
    DeviceFeatures.mHasLoadedServiceFeatures = true;
    if (features === undefined || features === null) {
      Log.w(DeviceFeatures.TAG, "initServiceFeatures() — no features provided");
      return;
    }
    for (let i = 0; i < features.length; i += 1) {
      const feature = features[i];
      if (
        feature !== undefined &&
        feature !== null &&
        feature.featureName !== undefined &&
        feature.featureName !== null &&
        feature.featureName !== ""
      ) {
        DeviceFeatures.mServiceFeatures.set(feature.featureName, feature);
      }
    }
    Log.d(
      DeviceFeatures.TAG,
      "initServiceFeatures() — captured device features"
    );
  }

  /**
   * Synchronous, fail-closed check for a service-controlled feature.
   *
   * Returns `false` when device features have not loaded (map empty) or the
   * feature is absent, so a service-controlled feature can never activate
   * before its data is available. With `attributeName` omitted the check is
   * feature presence; with it provided the named boolean attribute is read.
   *
   * @param {string} featureName - Device feature name
   * @param {string} [attributeName] - Boolean attribute; omit for presence
   * @returns {boolean} True when the feature is present/enabled
   */
  public static isServiceFeatureEnabled(
    featureName: string,
    attributeName?: string
  ): boolean {
    const feature = DeviceFeatures.mServiceFeatures.get(featureName);
    if (feature === undefined) {
      return false;
    }
    if (attributeName === undefined) {
      return true;
    }
    return parseFeatureAttributeBoolean(feature, attributeName, false);
  }

  /**
   * Source-state accessor for the Feature Activation Framework.
   *
   * Returns `null` when device features have not loaded yet (so the framework
   * can fall back to cached state), a definitive `boolean` once loaded: presence
   * when `attributeName` is omitted, or the parsed boolean attribute otherwise.
   *
   * @param {string} featureName - Device feature name
   * @param {string} [attributeName] - Boolean attribute; omit for presence
   * @returns {boolean | null} Fresh device-feature state, or null if not loaded
   */
  public static getServiceFeatureState(
    featureName: string,
    attributeName?: string
  ): boolean | null {
    if (!DeviceFeatures.mHasLoadedServiceFeatures) {
      return null;
    }
    const feature = DeviceFeatures.mServiceFeatures.get(featureName);
    if (feature === undefined) {
      return false;
    }
    if (attributeName === undefined) {
      return true;
    }
    return parseFeatureAttributeBoolean(feature, attributeName, false);
  }

  /**
   * Latest known NPVR allow-recording state, or `null` when no NPVR response
   * has been received this session. Ignores TTL on purpose: TTL governs when a
   * refetch happens, not what the last known value is.
   *
   * @returns {boolean | null} Last known NPVR enabled flag, or null if unknown
   */
  public static getNpvrRecordingState(): boolean | null {
    if (DeviceFeatures.mNpvrAllowRecording === null) {
      return null;
    }
    return DeviceFeatures.mNpvrAllowRecording.enabled;
  }

  /**
   * Extracts the `switchGroup` attribute from the `playbackConfig` device
   * feature and caches its parsed contents.
   *
   * The attribute value is a JSON object serialized into a string — e.g.
   * `{"default":"TG_ALL"}` — mapping a playback scenario to the switch
   * (transcode) group to request for it, with the `default` key carrying the
   * group to use when no scenario-specific entry exists.
   *
   * Unlike `msoServiceId`, an absent or malformed `playbackConfig` is not
   * fatal: every accessor below reports the gap (false / empty string) so
   * callers can fall back to their own default rather than fail sign-in.
   *
   * @param {FeatureResponse} aPlaybackConfig - The `playbackConfig` feature
   */
  public static initPlaybackConfigFeature(
    aPlaybackConfig: FeatureResponse
  ): void {
    DeviceFeatures.mSwitchGroups = new Map<string, string>();
    DeviceFeatures.mIsSwitchGroupValid = false;

    if (
      aPlaybackConfig === undefined ||
      aPlaybackConfig === null ||
      aPlaybackConfig.attributes === undefined ||
      aPlaybackConfig.attributes === null
    ) {
      Log.w(
        DeviceFeatures.TAG,
        "initPlaybackConfigFeature() — feature or attributes missing"
      );
      return;
    }

    let switchGroupValue: string = "";
    for (let i = 0; i < aPlaybackConfig.attributes.length; i += 1) {
      const attribute = aPlaybackConfig.attributes[i];
      if (
        attribute.attributeName !== undefined &&
        attribute.attributeName !== null &&
        attribute.attributeName === DeviceFeatures.ATTRIBUTE_SWITCH_GROUP &&
        attribute.attributeValue !== undefined &&
        attribute.attributeValue !== null
      ) {
        switchGroupValue = attribute.attributeValue.trim();
        break;
      }
    }

    if (switchGroupValue === "") {
      Log.w(
        DeviceFeatures.TAG,
        "initPlaybackConfigFeature() — switchGroup attribute missing or empty"
      );
      return;
    }

    DeviceFeatures.mSwitchGroups =
      DeviceFeatures.parseSwitchGroups(switchGroupValue);
    DeviceFeatures.mIsSwitchGroupValid = DeviceFeatures.mSwitchGroups.size > 0;

    if (DeviceFeatures.mIsSwitchGroupValid) {
      Log.d(
        DeviceFeatures.TAG,
        "initPlaybackConfigFeature() — switchGroup initialized"
      );
    }
  }

  /**
   * @returns {boolean} True when at least one switch group was resolved from
   * the `playbackConfig` feature.
   */
  public static isSwitchGroupValid(): boolean {
    return DeviceFeatures.mIsSwitchGroupValid;
  }

  /**
   * Resolves a switch group by key, falling back to the `default` entry when
   * the key carries no entry of its own.
   *
   * @param {string} [aKey] - Scenario key; defaults to `default`
   * @returns {string} The switch group (e.g. `TG_ALL`), or `""` when neither
   * the key nor `default` is present
   */
  public static getSwitchGroup(
    aKey: string = DeviceFeatures.SWITCH_GROUP_DEFAULT_KEY
  ): string {
    const value: string | undefined = DeviceFeatures.mSwitchGroups.get(aKey);
    if (value !== undefined) {
      return value;
    }

    const fallback: string | undefined = DeviceFeatures.mSwitchGroups.get(
      DeviceFeatures.SWITCH_GROUP_DEFAULT_KEY
    );
    return fallback !== undefined ? fallback : "";
  }

  /**
   * @returns {Readonly<Record<string, string>>} A copy of every switch group
   * keyed by scenario, empty when the feature was absent or malformed
   */
  public static getSwitchGroups(): Readonly<Record<string, string>> {
    return Object.fromEntries(DeviceFeatures.mSwitchGroups);
  }

  /**
   * Extracts the `autoStandbyInactivityTimeoutMinutes` attribute from the
   * `inactivityTime` device feature and caches it as milliseconds.
   *
   * Absence or a malformed value is tolerated — same as `playbackConfig` —
   * so {@link getPlaybackInactivityTimeoutMs} keeps returning the
   * `config.ts` fallback rather than blocking playback over it.
   *
   * @param {FeatureResponse} aInactivityTime - The `inactivityTime` feature
   */
  public static initInactivityTimeFeature(
    aInactivityTime: FeatureResponse
  ): void {
    DeviceFeatures.mIsPlaybackInactivityTimeoutValid = false;

    if (
      aInactivityTime === undefined ||
      aInactivityTime === null ||
      aInactivityTime.attributes === undefined ||
      aInactivityTime.attributes === null
    ) {
      Log.w(
        DeviceFeatures.TAG,
        "initInactivityTimeFeature() — feature or attributes missing"
      );
      return;
    }

    let rawMinutes: string = "";
    for (let i = 0; i < aInactivityTime.attributes.length; i += 1) {
      const attribute = aInactivityTime.attributes[i];
      if (
        attribute.attributeName !== undefined &&
        attribute.attributeName !== null &&
        attribute.attributeName ===
          DeviceFeatures.ATTRIBUTE_AUTO_STANDBY_INACTIVITY_TIMEOUT_MINUTES &&
        attribute.attributeValue !== undefined &&
        attribute.attributeValue !== null
      ) {
        rawMinutes = attribute.attributeValue.trim();
        break;
      }
    }

    if (rawMinutes === "") {
      Log.w(
        DeviceFeatures.TAG,
        "initInactivityTimeFeature() — autoStandbyInactivityTimeoutMinutes attribute missing or empty"
      );
      return;
    }

    const minutes: number = Number.parseInt(rawMinutes, 10);
    if (!Number.isFinite(minutes) || minutes <= 0) {
      Log.w(
        DeviceFeatures.TAG,
        "initInactivityTimeFeature() — autoStandbyInactivityTimeoutMinutes is not a positive integer"
      );
      return;
    }

    DeviceFeatures.mPlaybackInactivityTimeoutMs =
      minutes * MILLISECONDS_PER_MINUTE;
    DeviceFeatures.mIsPlaybackInactivityTimeoutValid = true;
    Log.d(
      DeviceFeatures.TAG,
      "initInactivityTimeFeature() — playback inactivity timeout initialized"
    );
  }

  /**
   * @returns {number} The playback inactivity OSD timeout in milliseconds —
   * from the `inactivityTime` device feature when valid, otherwise the
   * `config.ts` fallback (`PLAYBACK_INACTIVITY_OSD_TIMEOUT_MS`)
   */
  public static getPlaybackInactivityTimeoutMs(): number {
    return DeviceFeatures.mIsPlaybackInactivityTimeoutValid
      ? DeviceFeatures.mPlaybackInactivityTimeoutMs
      : PLAYBACK_INACTIVITY_OSD_TIMEOUT_MS;
  }

  /**
   * Parses the serialized `switchGroup` JSON object into a key → group map.
   * Non-string and empty values are dropped so callers never see a group they
   * cannot send to a service, and a parse failure yields an empty map rather
   * than propagating.
   */
  private static parseSwitchGroups(aRawValue: string): Map<string, string> {
    const switchGroups: Map<string, string> = new Map<string, string>();

    let parsed: unknown;
    try {
      parsed = JSON.parse(aRawValue);
    } catch (parseError: unknown) {
      Log.e(
        DeviceFeatures.TAG,
        "parseSwitchGroups() — failed to parse switchGroup JSON",
        parseError
      );
      return switchGroups;
    }

    if (
      parsed === null ||
      typeof parsed !== "object" ||
      Array.isArray(parsed)
    ) {
      Log.w(
        DeviceFeatures.TAG,
        "parseSwitchGroups() — switchGroup is not a JSON object"
      );
      return switchGroups;
    }

    const entries: Array<[string, unknown]> = Object.entries(
      parsed as Record<string, unknown>
    );
    for (let i = 0; i < entries.length; i += 1) {
      const [key, value] = entries[i];
      if (typeof value === "string" && value.trim() !== "") {
        switchGroups.set(key, value.trim());
      }
    }

    return switchGroups;
  }

  public static isNpvrAllowRecordingValid(): boolean {
    if (DeviceFeatures.mNpvrAllowRecording === null) {
      return false;
    }
    const elapsedMs: number =
      Date.now() - DeviceFeatures.mNpvrAllowRecordingTimestamp;
    const maxAgeMs: number = DeviceFeatures.mNpvrAllowRecording.maxAge * 1000;
    return elapsedMs < maxAgeMs;
  }

  public static async getNpvrAllowedRecording(
    interactor: IPostSigninInteractor,
    options?: TNetworkOptions
  ): Promise<boolean> {
    if (DeviceFeatures.isNpvrAllowRecordingValid())
      return DeviceFeatures.mNpvrAllowRecording!.enabled;

    try {
      const res = await interactor.fetchNpvrAllowRecording(options);
      DeviceFeatures.mNpvrAllowRecording = res;
      DeviceFeatures.mNpvrAllowRecordingTimestamp = Date.now();
      DiagnosticLogger.log(
        "device_features",
        "getNpvrAllowedRecording succeeded",
        DiagnosticSeverity.Info,
        { enabled: String(res.enabled) }
      );
      return res.enabled;
    } catch (error) {
      Log.e(
        DeviceFeatures.TAG,
        "getNpvrAllowedRecording() — failed to fetch NPVR allow recording",
        error
      );
      const errorMessage: string =
        OperationalLogger.describeErrorMessage(error);
      const errorCode: string =
        ErrorTypes.NPVR_ALLOW_RECORDING_REQUEST_FAILED_R504;

      OperationalLogger.logError({
        errorCode,
        errorMessage
      });
      return false;
    }
  }
}
